'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { InputField } from '@/shared/components/form/input-field';
import { Form } from '@/shared/components/form/form';
import { FormSubmit } from '@/shared/components/form/form-submit';
import { resolveErrorMessage } from '@/shared/helpers/resolve-error-message';
import { useLogin } from '@/features/auth/internal/hooks/use-login';
import { Button } from '@/shared/components/primitives/button';

export type LoginFormProps = {
  setRootError?: (message: string | null) => void;
  titleId: string;
  accountCreationHandler?: () => void;
  redirectUrl?: string;
  defaultEmail?: string;
};

export const LoginForm = ({
  setRootError,
  titleId,
  accountCreationHandler = () => {},
  redirectUrl,
  defaultEmail,
}: LoginFormProps) => {
  const t = useTranslations('Auth.login.form');
  const { register, handleSubmit, errors, isSubmitting, onSubmit, tooManyRetries } = useLogin(
    redirectUrl,
    defaultEmail,
  );

  const submitDisabled = tooManyRetries || isSubmitting;

  useEffect(() => {
    if (setRootError && errors.root?.message) {
      const message = t(errors.root?.message);
      setRootError(message);
    } else if (setRootError) {
      setRootError(null);
    }
  }, [errors.root?.message, setRootError, t]);

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className='flex w-full max-w-96 flex-col gap-2'
      aria-labelledby={titleId}
    >
      <InputField
        isRequired
        inputProps={register('email')}
        errorMessage={resolveErrorMessage(errors.email, t)}
        type='email'
        placeholder={t('email')}
        autoComplete='email'
      />
      <InputField
        isRequired
        inputProps={register('password')}
        errorMessage={resolveErrorMessage(errors.password, t)}
        type='password'
        placeholder={t('password')}
        autoComplete='current-password'
      />
      <div className='flex w-full flex-row justify-between'>
        <Link prefetch={false} className='text-text-muted ml-auto underline' href='/reset-password'>
          {t('forgotYourPassword')}
        </Link>
      </div>
      <FormSubmit aria-label={t('signIn')} className='mx-auto mt-4' disabled={submitDisabled}>
        {isSubmitting ? t('signingIn') : t('signIn')}
      </FormSubmit>
      <div className='mx-auto mt-6 flex items-center gap-3'>
        {t('dontHaveAccount')}{' '}
        <Button
          type='button'
          variant='noStyle'
          size='noStyle'
          className='text-secondary hover:!text-secondary-hover underline'
          onClick={accountCreationHandler}
        >
          {t('signUp')}.
        </Button>
      </div>
    </Form>
  );
};
